import React from "react";
import { Box, Button, Stack, Typography, Chip } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LoyaltyRoundedIcon from "@mui/icons-material/LoyaltyRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";

import { normalizeHttpsUrl, useDataStore } from "../dataStore";
import OptimizedConfigImage from "../components/OptimizedConfigImage";

const STORE_BADGE_ASPECT_RATIO = "10 / 3";
const BUNDLED_APP_STORE_BADGE = "appstore-badge.png";
const BUNDLED_PLAY_STORE_BADGE = "googleplay-badge.png";

function getBundledAssetPath(filename) {
  const publicUrl = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${publicUrl}/assets/${filename}`;
}

function StoreLink({
  href,
  badge,
  cropPaddedBadge = false,
  label,
  brandTheme,
}) {
  const [badgeFailed, setBadgeFailed] = React.useState(false);

  React.useEffect(() => {
    setBadgeFailed(false);
  }, [badge]);

  if (!href) return null;

  const showBadge = Boolean(badge && !badgeFailed);

  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      sx={{
        display: "flex",
        flex: { xs: "1 1 0", md: "0 0 auto" },
        minWidth: 0,
        minHeight: 48,
        width: { xs: "100%", md: 198 },
        maxWidth: { xs: 154, md: 198 },
        p: showBadge ? 0 : 1.25,
        borderRadius: "10px",
        overflow: "hidden",
        lineHeight: 1.2,
        fontWeight: 900,
        textAlign: "center",
        color: showBadge ? "inherit" : brandTheme.onCta,
        bgcolor: showBadge ? "transparent" : brandTheme.cta,
        boxShadow: showBadge
          ? "none"
          : "0 12px 26px rgba(17,17,17,0.16)",
        transition: "transform 160ms ease, filter 160ms ease",
        "&:hover": {
          bgcolor: showBadge ? "transparent" : brandTheme.cta,
          transform: "translateY(-2px)",
          filter: "brightness(1.04)",
        },
        "&:focus-visible": {
          outline: `3px solid ${brandTheme.primary}`,
          outlineOffset: 3,
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": {
            transform: "none",
          },
        },
      }}
    >
      {showBadge ? (
        <Box
          data-testid="store-badge-viewport"
          style={{ aspectRatio: STORE_BADGE_ASPECT_RATIO }}
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            borderRadius: "8px",
          }}
        >
          <Box
            component={cropPaddedBadge ? "img" : OptimizedConfigImage}
            data-testid="store-badge-image"
            src={badge}
            alt=""
            onError={() => setBadgeFailed(true)}
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              maxWidth: "none",
              objectFit: cropPaddedBadge ? "cover" : "contain",
              objectPosition: "center",
            }}
          />
        </Box>
      ) : (
        label
      )}
    </Button>
  );
}

const PHONE_ASPECT_RATIO = "9 / 19.5";

function PhoneScreen({ src, alt = "", testId, sx = {} }) {
  const [usesWideCanvas, setUsesWideCanvas] = React.useState(false);

  React.useEffect(() => {
    setUsesWideCanvas(false);
  }, [src]);

  if (!src) return null;

  return (
    <Box
      data-testid={testId}
      style={{ aspectRatio: PHONE_ASPECT_RATIO }}
      sx={{
        position: "relative",
        flexShrink: 0,
        overflow: "hidden",
        borderRadius: "17% / 8%",
        ...sx,
      }}
    >
      <Box
        component={OptimizedConfigImage}
        src={src}
        alt={alt}
        onLoad={(event) => {
          const { naturalWidth, naturalHeight } = event.currentTarget;
          setUsesWideCanvas(
            Boolean(naturalHeight && naturalWidth / naturalHeight >= 1)
          );
        }}
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          maxWidth: "none",
          objectFit: usesWideCanvas ? "cover" : "contain",
          objectPosition: "center",
        }}
      />
    </Box>
  );
}

export default function AppDownload() {
  const {
    brandName,
    publicConfigReady,
    publicImagesReady,
    appStoreUrl,
    playStoreUrl,
    appDownloadImages = {},
    brandTheme,
    t,
  } = useDataStore();
  const appStoreHref = normalizeHttpsUrl(appStoreUrl);
  const playStoreHref = normalizeHttpsUrl(playStoreUrl);
  const hasStoreLinks = Boolean(appStoreHref || playStoreHref);

  if (!hasStoreLinks) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: {
            xs: "calc(100dvh - 10px)",
            sm: "calc(100dvh - 16px)",
            md: "calc(100dvh - 20px)",
          },
          marginLeft: "calc(50% - 50vw)",
          display: "grid",
          placeItems: "center",
          px: { xs: 2, md: 4 },
          py: { xs: 5, md: 7 },
          bgcolor: brandTheme.background,
          background: `linear-gradient(145deg, ${brandTheme.background} 0%, ${brandTheme.surface} 100%)`,
        }}
      >
        <Stack
          role="status"
          aria-live="polite"
          spacing={1.5}
          alignItems="center"
          sx={{
            width: "100%",
            maxWidth: 620,
            p: { xs: 3, sm: 4, md: 5 },
            textAlign: "center",
            borderRadius: { xs: "18px", md: "24px" },
            bgcolor: brandTheme.surface,
            border: "1px solid rgba(124,45,18,0.10)",
            boxShadow: "0 22px 58px rgba(33,20,12,0.12)",
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 58,
              height: 58,
              display: "grid",
              placeItems: "center",
              borderRadius: "16px",
              color: brandTheme.accent,
              bgcolor: brandTheme.background,
              border: "1px solid rgba(124,45,18,0.10)",
            }}
          >
            <PhoneIphoneRoundedIcon />
          </Box>

          <Typography
            component="h1"
            sx={{
              color: brandTheme.text,
              fontWeight: 950,
              fontSize: { xs: 30, sm: 38 },
              lineHeight: 1.08,
            }}
          >
            {t("appDownload.unavailableTitle")}
          </Typography>

          <Typography
            color="text.secondary"
            sx={{ maxWidth: 500, lineHeight: 1.7 }}
          >
            {t("appDownload.unavailable")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  const appName = brandName
    ? `${brandName} App`
    : publicConfigReady
      ? t("appDownload.app")
      : "";
  const appLabel = appName || t("appDownload.app");
  const appPreviewAlt = brandName
    ? `${t("appDownload.alt")} ${brandName}`
    : t("appDownload.alt");
  const bgImage = publicImagesReady ? appDownloadImages.background || "" : "";
  const screen1 = publicImagesReady ? appDownloadImages.screen1 || "" : "";
  const screen2 = publicImagesReady ? appDownloadImages.screen2 || "" : "";
  const screen3 = publicImagesReady ? appDownloadImages.screen3 || "" : "";
  const configuredAppStoreBadge = publicImagesReady
    ? appDownloadImages.appStoreBadge || ""
    : "";
  const configuredPlayStoreBadge = publicImagesReady
    ? appDownloadImages.playStoreBadge || ""
    : "";
  const useBundledAppStoreBadge = Boolean(
    publicImagesReady && appStoreHref && !configuredAppStoreBadge
  );
  const useBundledPlayStoreBadge = Boolean(
    publicImagesReady && playStoreHref && !configuredPlayStoreBadge
  );
  const appStoreBadge =
    configuredAppStoreBadge ||
    (useBundledAppStoreBadge
      ? getBundledAssetPath(BUNDLED_APP_STORE_BADGE)
      : "");
  const playStoreBadge =
    configuredPlayStoreBadge ||
    (useBundledPlayStoreBadge
      ? getBundledAssetPath(BUNDLED_PLAY_STORE_BADGE)
      : "");
  const primaryScreen = screen1 || screen2 || screen3;
  const hasScreens = Boolean(primaryScreen);

  const benefits = [
    {
      icon: <BoltRoundedIcon fontSize="small" />,
      label: t("appDownload.fast"),
    },
    {
      icon: <LoyaltyRoundedIcon fontSize="small" />,
      label: t("appDownload.loyalty"),
    },
    {
      icon: <PhoneIphoneRoundedIcon fontSize="small" />,
      label: t("appDownload.simple"),
    },
  ];

  return (
    <Box
      sx={{
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        minHeight: {
          xs: "calc(100dvh - 10px)",
          sm: "calc(100dvh - 16px)",
          md: "calc(100dvh - 20px)",
        },
        position: "relative",
        overflow: "hidden",
        marginTop: 0,
        marginBottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, md: 4 },
        pt: { xs: 2.5, md: 6 },
        pb: { xs: 5, md: 6 },
        background: `linear-gradient(135deg, ${brandTheme.dark} 0%, ${brandTheme.cta} 100%)`,
      }}
    >
      {bgImage ? (
        <Box
          component={OptimizedConfigImage}
          src={bgImage}
          alt=""
          loading="eager"
          decoding="async"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : null}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,247,237,0.03) 54%, rgba(17,17,17,0.14) 100%), linear-gradient(90deg, rgba(67,31,18,0.82) 0%, rgba(124,45,18,0.56) 48%, rgba(15,118,110,0.24) 100%)",
          zIndex: 1,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: hasScreens ? 1220 : 720,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: hasScreens
              ? "520px minmax(0, 1fr)"
              : "minmax(0, 1fr)",
          },
          gap: { xs: 2, lg: 5 },
          alignItems: "center",
          justifyContent: "center",
          "@media (min-width: 700px) and (max-width: 1199.95px)": {
            maxWidth: hasScreens ? 960 : 720,
            gridTemplateColumns: hasScreens
              ? "minmax(0, 500px) minmax(220px, 340px)"
              : "minmax(0, 1fr)",
            gap: "24px",
          },
        }}
      >
        {/* promo card */}
        <Box
          sx={{
            position: "relative",
            zIndex: 3,
            bgcolor: brandTheme.surface,
            borderRadius: { xs: "14px", md: "18px" },
            p: { xs: 2, sm: 2.5, md: 4 },
            minHeight: hasStoreLinks
              ? { xs: 410, sm: 450, md: 490, lg: "auto" }
              : { xs: 360, sm: 400, md: 430, lg: "auto" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            boxShadow: "0 30px 86px rgba(33,20,12,0.30)",
            border: "1px solid rgba(255,255,255,0.52)",
            backdropFilter: "blur(18px)",
          }}
        >
          <Stack spacing={{ xs: 1.1, md: 1.7 }}>
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: brandTheme.onPrimary,
                background: brandTheme.primary,
                border: "1px solid rgba(124,45,18,0.12)",
                boxShadow: "0 12px 28px rgba(194,65,12,0.12)",
              }}
            >
              <DownloadRoundedIcon />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: 14,
                  color: brandTheme.muted,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                <Box component="span" sx={{ visibility: appName ? "visible" : "hidden" }}>
                  {appLabel}
                </Box>
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  fontWeight: 950,
                  fontSize: { xs: 34, sm: 42, md: 54 },
                  lineHeight: 1.02,
                  letterSpacing: 0,
                  color: brandTheme.text,
                }}
              >
                {t("appDownload.install")}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 850,
                color: brandTheme.text,
                fontSize: { xs: 17, md: 19 },
                lineHeight: 1.35,
              }}
            >
              {t("appDownload.subtitle")}
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                lineHeight: 1.65,
                fontSize: { xs: 15, md: 16 },
              }}
            >
              {t("appDownload.copy")}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: "wrap",
                gap: 1,
                pt: 0.5,
              }}
            >
              {benefits.map((benefit) => (
                <Chip
                  key={benefit.label}
                  icon={benefit.icon}
                  label={benefit.label}
                  sx={{
                    fontWeight: 800,
                    bgcolor: brandTheme.background,
                    color: brandTheme.text,
                    border: "1px solid rgba(124,45,18,0.10)",
                    "& .MuiChip-icon": {
                      color: brandTheme.accent,
                    },
                  }}
                />
              ))}
            </Stack>

            {hasStoreLinks ? (
              <>
                <Stack
                  direction="row"
                  spacing={0}
                  sx={{
                    mt: { xs: 0.45, md: 1.15 },
                    alignItems: "stretch",
                    justifyContent: { xs: "center", md: "flex-start" },
                    flexWrap: "wrap",
                    gap: 1,
                    maxWidth: "100%",
                    position: "relative",
                    zIndex: 4,
                  }}
                >
                  <StoreLink
                    href={appStoreHref}
                    badge={appStoreBadge}
                    cropPaddedBadge={useBundledAppStoreBadge}
                    label={t("appDownload.appStore")}
                    brandTheme={brandTheme}
                  />
                  <StoreLink
                    href={playStoreHref}
                    badge={playStoreBadge}
                    cropPaddedBadge={useBundledPlayStoreBadge}
                    label={t("appDownload.playStore")}
                    brandTheme={brandTheme}
                  />
                </Stack>

                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontSize: 12.5,
                    color: "text.secondary",
                    lineHeight: 1.5,
                    pt: 0.5,
                  }}
                >
                  {t("appDownload.availability")}
                </Typography>
              </>
            ) : null}
          </Stack>
        </Box>

        {/* mobile phone preview */}
        <Box
          sx={{
            display: hasScreens ? "flex" : "none",
            "@media (min-width: 700px)": {
              display: "none",
            },
            justifyContent: "center",
            alignItems: "center",
            minWidth: 0,
            py: 1,
            pointerEvents: "none",
          }}
        >
          <PhoneScreen
            src={primaryScreen}
            alt={appPreviewAlt}
            testId="app-screen-mobile"
            sx={{
              width: "min(48vw, 190px)",
              filter: "drop-shadow(0 28px 54px rgba(33,20,12,0.42))",
            }}
          />
        </Box>

        {/* tablet phone preview */}
        <Box
          sx={{
            display: "none",
            "@media (min-width: 700px) and (max-width: 1199.95px)": {
              display: hasScreens ? "flex" : "none",
            },
            justifyContent: "center",
            alignItems: "center",
            minWidth: 0,
            minHeight: 470,
            pointerEvents: "none",
          }}
        >
          <PhoneScreen
            src={primaryScreen}
            alt={appPreviewAlt}
            testId="app-screen-tablet"
            sx={{
              width: "min(100%, 260px)",
              filter: "drop-shadow(0 36px 68px rgba(33,20,12,0.42))",
            }}
          />
        </Box>

        {/* phones visual */}
        <Box
          sx={{
            display: hasScreens ? { xs: "none", lg: "flex" } : "none",
            position: "relative",
            height: 640,
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          {screen2 && screen2 !== primaryScreen ? (
            <PhoneScreen
              src={screen2}
              alt=""
              testId="app-screen-desktop-secondary"
              sx={{
                position: "absolute",
                width: 240,
                transform: "rotate(-8deg)",
                left: 16,
                top: 60,
                filter: "drop-shadow(0 40px 60px rgba(33,20,12,0.42))",
              }}
            />
          ) : null}

          {primaryScreen ? (
            <PhoneScreen
              src={primaryScreen}
              alt={appPreviewAlt}
              testId="app-screen-desktop-primary"
              sx={{
                position: "absolute",
                width: 263,
                zIndex: 2,
                filter: "drop-shadow(0 42px 84px rgba(33,20,12,0.50))",
              }}
            />
          ) : null}

          {screen3 && screen3 !== primaryScreen ? (
            <PhoneScreen
              src={screen3}
              alt=""
              testId="app-screen-desktop-tertiary"
              sx={{
                position: "absolute",
                width: 240,
                transform: "rotate(8deg)",
                right: 16,
                top: 80,
                filter: "drop-shadow(0 40px 60px rgba(33,20,12,0.42))",
              }}
            />
          ) : null}
        </Box>

      </Box>
    </Box>
  );
}
